import { Link, useLocation } from "wouter";
import { useClerk } from "@clerk/react";
import { clearRheumHubLocalStorage } from "@/lib/local-storage-keys";
import { useEffect } from "react";
import { useIsAdmin } from "@/hooks/use-is-admin";
import { GlobalSearch } from "@/components/ui/global-search";
import { DashboardHeader } from "@/components/layout/dashboard-header";
import {
  LayoutDashboard,
  Activity,
  Hand,
  ShieldCheck,
  Brain,
  ClipboardList,
  Cpu,
  BarChart3,
  BookOpen,
  MessageSquare,
  LogOut,
  Settings,
  Users,
  GraduationCap,
  TrendingUp,
  Shield,
  Star,
  Award,
  Layers,
  FileText,
  Lightbulb,
  AlertTriangle,
  FlaskConical,
  CalendarDays,
} from "lucide-react";
import { LastSignInBanner } from "@/components/ui/last-signin-banner";
import { SessionNoticeBanner } from "@/components/ui/session-notice-banner";
import { HcpVerificationBanner } from "@/components/ui/hcp-verification-banner";
import { LegalFooter } from "@/components/layout/legal-footer";

export function OTLayout({ children, title }: { children: React.ReactNode; title: string }) {
  const [location] = useLocation();
  const { signOut } = useClerk();
  const isAdmin = useIsAdmin();

  useEffect(() => {
    document.title = `${title} | RheumHub`;
    return () => { document.title = "RheumHub"; };
  }, [title]);

  const clinicalItems = [
    { href: "/ot", label: "Dashboard", icon: LayoutDashboard },
    { href: "/ot/hand-assessment", label: "Hand Assessment & Splinting", icon: Hand },
    { href: "/ot/joint-protection", label: "Joint Protection", icon: ShieldCheck },
    { href: "/ot/fatigue-management", label: "Fatigue Management", icon: Brain },
    { href: "/ot/adl", label: "ADL & Functional Independence", icon: Users },
    { href: "/ot/assistive-technology", label: "Assistive Technology", icon: Cpu },
  ];

  const assessmentItems = [
    { href: "/ot/outcome-measures", label: "Outcome Measures", icon: BarChart3 },
  ];

  const guidelinesItems = [
    { href: "/ot/guidelines", label: "RCOT & NICE Guidelines", icon: BookOpen },
  ];

  const professionalItems = [
    { href: "/ot/chat", label: "AI Clinical Assistant", icon: MessageSquare },
  ];

  const cpdItems = [
    { href: "/ot/learn", label: "Learning Hub", icon: BookOpen },
    { href: "/ot/cpd", label: "CPD Activity Log", icon: GraduationCap },
    { href: "/ot/cpd/revalidation", label: "HCPC Revalidation", icon: Shield },
    { href: "/ot/cpd/rcot-framework", label: "RCOT CPD Framework", icon: Layers },
    { href: "/ot/cpd/advanced-practice", label: "Advanced Practice Portfolio", icon: Star },
    { href: "/ot/cpd/specialist-section", label: "SS Rheumatology Hub", icon: Users },
    { href: "/ot/cpd/rcot-learning", label: "RCOT College CPD", icon: BookOpen },
    { href: "/ot/cpd/reflection", label: "Reflection Tool", icon: Lightbulb },
  ];

  const careerItems = [
    { href: "/ot/career", label: "Career Pathways", icon: TrendingUp },
    { href: "/ot/career/band-progression", label: "Band Progression", icon: ClipboardList },
    { href: "/ot/career/consultant", label: "Consultant OT", icon: Star },
    { href: "/ot/career/frcot", label: "FRCOT Fellowship", icon: Award },
    { href: "/ot/career/academic", label: "Academic OT", icon: FlaskConical },
    { href: "/ot/career/ss-rheumatology", label: "SS Rheumatology Career", icon: FileText },
  ];

  const eventsItems = [
    { href: "/ot/events", label: "Events", icon: CalendarDays },
  ];

  const governanceItems = [
    { href: "/hcp/safety-report", label: "Report Safety Concern", icon: AlertTriangle },
    { href: "/ot/settings", label: "Your Data", icon: Settings },
  ];

  const isActive = (href: string) =>
    location === href || (location.startsWith(href + "/") && href !== "/ot");

  const renderNavItem = (item: { href: string; label: string; icon: React.ElementType }) => {
    const active = isActive(item.href);
    return (
      <Link
        key={item.href}
        href={item.href}
        className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
          active
            ? "bg-[#f0abcb]/15 text-[#f0abcb]"
            : "hover:bg-white/10 hover:text-white"
        }`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <item.icon className={`h-4 w-4 shrink-0 ${active ? "text-[#f0abcb]" : "text-white/35"}`} />
        {item.label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <SessionNoticeBanner />
      <LastSignInBanner />
      <HcpVerificationBanner />
      <div className="flex flex-1">
        <aside className="w-64 bg-[#480d2f] text-white/75 flex flex-col fixed inset-y-0 z-20">
          <div className="p-6 flex items-center gap-3 text-white">
            <Activity className="h-6 w-6 text-[#f0abcb]" />
            <span className="text-xl font-bold tracking-tight">RheumHub</span>
          </div>

          <div className="px-6 py-2 text-xs font-semibold uppercase tracking-wider text-[#f0abcb]/60">
            OT Portal
          </div>

          <nav className="sidebar-nav flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
            <div className="pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Clinical Tools
              </div>
            </div>
            {clinicalItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Patient Education
              </div>
            </div>
            {assessmentItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Guidelines & Resources
              </div>
            </div>
            {guidelinesItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Professional
              </div>
            </div>
            {professionalItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                CPD Portfolio
              </div>
            </div>
            {cpdItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Career
              </div>
            </div>
            {careerItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Events
              </div>
            </div>
            {eventsItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Governance
              </div>
            </div>
            {governanceItems.map((item) => renderNavItem(item))}
          </nav>

          <div className="p-3 border-t border-white/10 space-y-0.5">
            {isAdmin && (
              <Link
                href="/admin"
                className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                  location.startsWith("/admin")
                    ? "bg-[#7bb8ff]/15 text-[#7bb8ff]"
                    : "hover:bg-white/10 hover:text-white text-white/35"
                }`}
              >
                <Users className="h-4 w-4 shrink-0" />
                Admin Panel
              </Link>
            )}
            <button
              onClick={() => { clearRheumHubLocalStorage(); signOut({ redirectUrl: "/" }); }}
              className="flex w-full items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium hover:bg-white/10 transition-colors text-white/35 hover:text-white"
              data-testid="nav-signout"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              Sign out
            </button>
          </div>
        </aside>

        <main className="flex-1 pl-64 flex flex-col min-h-screen">
          <DashboardHeader title={title} profileLink="/ot/profile" />
          <div className="flex-1 p-8">
            <div className="max-w-6xl mx-auto">
              {children}
            </div>
          </div>
          <LegalFooter
            copyright="© RheumHub · For UK occupational therapists only · AI responses do not constitute clinical advice · Grounded in RCOT Code, NICE, COT guidance"
            extraLinks={[{ label: "Clinical Safety", href: "/clinical-safety" }]}
          />
        </main>
      </div>
    </div>
  );
}
